import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  FileText,
  Users,
  Settings as SettingsIcon,
  LogOut,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/properties", label: "Properties", icon: Building2, testId: "nav-properties" },
  { to: "/documents", label: "Documents", icon: FileText, testId: "nav-documents" },
  { to: "/contacts", label: "Contacts", icon: Users, testId: "nav-contacts" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

export function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    onClose();
    navigate("/login");
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        data-testid="sidebar-backdrop"
        onClick={onClose}
        className={cn(
          "md:hidden fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 transition-opacity duration-200",
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        aria-hidden={!mobileOpen}
      />

      <aside
        data-testid="app-sidebar"
        className={cn(
          "fixed inset-y-0 left-0 w-64 bg-slate-900 dark:bg-slate-950 border-r border-slate-800 z-40 flex flex-col transform transition-transform duration-300 ease-out",
          "md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="px-6 py-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-sm bg-white dark:bg-slate-100 flex items-center justify-center">
              <span className="font-display text-slate-900 font-bold text-sm">F</span>
            </div>
            <div className="leading-tight">
              <p className="font-display text-white font-semibold tracking-tight">Factor</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Operations</p>
            </div>
          </div>
          {/* Close button — mobile only */}
          <button
            data-testid="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close menu"
            className="md:hidden h-8 w-8 flex items-center justify-center rounded-sm text-slate-400 hover:text-white hover:bg-slate-800/60"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors duration-150",
                  isActive
                    ? "bg-slate-800 text-white"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                )
              }
            >
              <item.icon size={16} strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-slate-800 space-y-3">
          <div className="px-3">
            <div className="flex items-center gap-1.5 mb-0.5">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Signed in</p>
              <span
                data-testid="sidebar-role-badge"
                className={cn(
                  "inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest border",
                  user?.role === "admin"
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                    : "bg-sky-500/10 text-sky-400 border-sky-500/20"
                )}
              >
                {user?.role || "guest"}
              </span>
            </div>
            <p className="text-sm text-slate-200 truncate" data-testid="sidebar-user-email">
              {user?.email || "demo@factor.ops"}
            </p>
          </div>
          <button
            data-testid="sidebar-logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
          >
            <LogOut size={16} strokeWidth={1.75} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
